// Deterministic reader for portable-extinguisher spec language (CSI 10 44 00 style). Rules, not a
// model: ratings, capacities, agents, "other than" exclusions, a basis of design, numbered
// alternates, and two-unit combinations. Anything it cannot read is returned as unparsed text for
// the agent to convert by hand. Parsed requirements carry the sentence they came from.

import type { Requirement } from "./types";
import type { SpecOption } from "./resolve";

export type ParsedSpec = {
  tag?: string;
  options: SpecOption[];
  /** The main clause's product requirements (what "does this product meet the spec" checks). */
  primary: Requirement[];
  unparsed: string[];
  notes: string[];
};

const FAMILY = "portable_fire_extinguisher" as const;

const RATING = /(\d+)\s*-?\s*A\s*:\s*(\d+)\s*-?\s*B\s*:\s*C|(\d+)\s*-?\s*B\s*:\s*C|\b(\d+)\s*-?\s*A\b(?![-:\w])/i;
const CAPACITY_LB = /(\d+(?:\.\d+)?)\s*-?\s*(?:lb|lbs|pound)/i;
const CAPACITY_GAL = /(\d+(?:\.\d+)?)\s*-?\s*(?:gal|gallon)/i;

function agentOf(text: string): { agent?: string; agentName?: string; exclude?: string[] } {
  const t = text.toLowerCase();
  const out: { agent?: string; agentName?: string; exclude?: string[] } = {};
  const excl = /other than\s+([^.;]+)/i.exec(text);
  if (excl) {
    const names = excl[1]!.split(/\s*(?:,|and|or)\s*/i).map((s) => s.trim()).filter(Boolean);
    out.exclude = names.map((n) => n.replace(/\s*type$/i, "").replace(/halotron\s*1\b/i, "Halotron I"));
  }
  if (/carbon dioxide|co₂|co2/.test(t) && !/other than[^.]*co2/.test(t) && !excl) out.agent = "carbon dioxide";
  else if (/\bwater\b/.test(t) && /extinguisher/.test(t) && !/(?:^|\s)and\s/.test(t.slice(0, 5))) out.agent = "water";
  else if (/dry chemical|\babc\b/.test(t)) out.agent = "ABC dry chemical";
  else if (/clean[- ]agent|halocarbon|halotron|hcfc|hfc|clean agent/.test(t)) out.agent = "clean agent";
  if (/halotron/.test(t) && !excl) out.agentName = "Halotron";
  return out;
}

let seq = 0;
const rid = (p: string) => `${p}-${++seq}`;

function reqsFrom(text: string, section: string, doc: string): Requirement[] {
  const src = { kind: "spec" as const, document: doc, section, text: text.trim().slice(0, 300) };
  const out: Requirement[] = [];
  const mk = (attribute: string, operator: Requirement["operator"], value: unknown, unit?: string): Requirement => ({ id: rid(section), appliesTo: FAMILY, attribute, operator, value, unit, source: src });
  const a = agentOf(text);
  if (a.agent) out.push(mk("agent", "eq", a.agent));
  if (a.agentName) out.push(mk("agent_name", "one_of", ["Halotron I", "Halotron"]));
  if (a.exclude?.length) out.push(mk("agent_name", "not_one_of", a.exclude));
  const r = RATING.exec(text);
  if (r) {
    const rating = r[1] ? `${r[1]}-A:${r[2]}-B:C` : r[3] ? `${r[3]}-B:C` : `${r[4]}-A`;
    out.push(mk("extinguisher_class_rating", "meets_rating", rating));
  }
  const c = CAPACITY_LB.exec(text);
  if (c) out.push(mk("capacity_lb", "gte", Number(c[1]), "lb"));
  const g = CAPACITY_GAL.exec(text);
  if (g && !c) out.push(mk("capacity_gal", "gte", Number(g[1]), "gal"));
  if (/steel (?:container|cylinder)/i.test(text)) out.push(mk("cylinder_material", "eq", "steel"));
  if (/powder[- ]coat/i.test(text)) out.push(mk("finish", "one_of", ["powder coat", "polyester powder coat", "polyester epoxy powder coat"]));
  if (/pressure[- ]indicating gauge|pressure gauge/i.test(text)) out.push(mk("pressure_gauge", "is_true", true));
  if (/\bul[- ]rated|ul listed|ul-listed/i.test(text)) out.push(mk("ul_listed", "is_true", true));
  return out;
}

export function parseSpecText(raw: string, document = "Specification (as pasted)"): ParsedSpec {
  seq = 0;
  const text = raw.replace(/\r/g, "").replace(/[“”]/g, '"').replace(/ /g, " ");
  const notes: string[] = [];
  const unparsed: string[] = [];
  const tag = /\b(FE\s*-?\s*\d+[A-Z]?)\b/i.exec(text)?.[1]?.replace(/\s+/g, "");
  // Split into the main clause and numbered sub-clauses ("1. Basis of Design…", "2. Alternate Model: …").
  const parts = text.split(/\n\s*(?=\d+\.\s)/).map((p) => p.trim()).filter(Boolean);
  const main = parts[0] ?? "";
  const subs = parts.slice(1);
  const primary = reqsFrom(main, "main", document);
  if (primary.length === 0) unparsed.push(main.slice(0, 300));
  if (/HFC Blend B/i.test(main)) notes.push('The spec says "HFC Blend B"; Halotron I (including Amerex 398) is an HCFC blend. Likely a drafting error — confirm with the engineer of record.');

  const options: SpecOption[] = [];
  let bodReqs: Requirement[] = primary;
  for (const s of subs) {
    const num = /^(\d+)\./.exec(s)?.[1] ?? String(options.length + 1);
    const label = s.replace(/^\d+\.\s*/, "").replace(/\s+/g, " ").slice(0, 160);
    const bod = /Basis[- ]of[- ]Design(?: Model)?:\s*"?([^"\n]+?)"?\s+by\s+([A-Za-z][A-Za-z&. ]+?)(?:\s+Corporation|\s+Corp\.?|\s+Inc\.?|\s+Co\.?)?\s*\.?\s*$/i.exec(s);
    if (bod) {
      const modelRaw = bod[1]!.trim();
      const model = (/#\s*([A-Za-z0-9-]+)/.exec(modelRaw)?.[1] ?? modelRaw.split(/[,\s]+/).pop() ?? modelRaw).replace(/^#/, "");
      options.push({ id: `bod-${num}`, label: `Basis of design: ${bod[2]!.trim()} ${model}`, kind: "basis_of_design", basisOfDesign: { manufacturer: bod[2]!.trim(), model }, requirements: bodReqs.map((r) => ({ ...r, id: rid("bod") })), source: { kind: "spec", document, section: num, text: s.slice(0, 300) } });
      continue;
    }
    if (/Combination of/i.test(s)) {
      const halves = s.split(/\s+(?:and|with|plus)\s+(?=(?:a\s+)?(?:stored|water|co2|co₂|carbon|dry|clean))/i);
      const slots = halves.map((h, i) => ({ id: `unit-${i + 1}`, label: h.replace(/^.*?Combination of\s*/i, "").replace(/\s+/g, " ").slice(0, 80), requirements: reqsFrom(h.replace(/^.*?Combination of\s*/i, ""), `${num}.${i + 1}`, document) })).filter((sl) => sl.requirements.length);
      if (slots.length >= 2) {
        options.push({ id: `alt-${num}`, label: `Alternate ${num}: ${label.replace(/^Alternate Model:\s*/i, "")}`, kind: "assembly", requirements: [], slots, source: { kind: "spec", document, section: num, text: s.slice(0, 300) } });
        continue;
      }
    }
    const reqs = reqsFrom(s, num, document);
    // Alternates inherit the main clause's rating and capacity unless they state their own.
    const inherited = primary.filter((r) => ["extinguisher_class_rating", "capacity_lb"].includes(r.attribute) && !reqs.some((x) => x.attribute === r.attribute)).map((r) => ({ ...r, id: rid(`${num}-inh`) }));
    if (reqs.length) options.push({ id: `alt-${num}`, label: `Alternate ${num}: ${label.replace(/^Alternate Model:\s*/i, "")}`, kind: "alternate", requirements: [...reqs, ...inherited], source: { kind: "spec", document, section: num, text: s.slice(0, 300) } });
    else unparsed.push(s.slice(0, 300));
  }
  if (options.length === 0 && primary.length) {
    options.push({ id: "spec", label: tag ? `${tag} requirements` : "Specified requirements", kind: "alternate", requirements: primary.map((r) => ({ ...r, id: rid("spec") })), source: { kind: "spec", document, text: main.slice(0, 300) } });
  }
  return { tag, options, primary, unparsed, notes };
}
